import { generatePlannerPDF } from '../utils/pdfGenerator';
import { plannerStorageService } from './plannerStorageService';
import { supabase } from '../lib/supabase';

/**
 * Service for regenerating planners from database configurations
 * Handles data transformation and progress tracking
 */
export const plannerRegenerationService = {
  /**
   * Transforms database configuration to pdfGenerator.js format
   * @param {Object} dbConfig - Configuration from planner_configurations table
   * @returns {Object} - Formatted plannerData object for pdfGenerator
   */
  transformDatabaseConfig(dbConfig) {
    try {
      // Parse layout_type if it's a string
      let layoutConfig = {};
      if (typeof dbConfig?.layout_type === 'string') {
        layoutConfig = JSON.parse(dbConfig?.layout_type);
      } else {
        layoutConfig = dbConfig?.layout_type || {};
      }

      // Parse configuration_data with enhanced error handling
      let configData = {};
      if (typeof dbConfig?.configuration_data === 'string') {
        try {
          // Try to parse as JSON
          configData = JSON.parse(dbConfig?.configuration_data);
        } catch (parseError) {
          // If parsing fails, check if it's a simple string value
          console.warn('PDF_PROGRESS: WARNING - configuration_data is not valid JSON, using default configuration');
          console.warn('PDF_PROGRESS: Raw configuration_data value:', dbConfig?.configuration_data);
          
          // Use default empty object and let fallback values handle it
          configData = {};
        }
      } else if (typeof dbConfig?.configuration_data === 'object' && dbConfig?.configuration_data !== null) {
        // If it's already an object, use it directly
        configData = dbConfig?.configuration_data;
      } else {
        // For any other type, use empty object
        console.warn('PDF_PROGRESS: WARNING - configuration_data is unexpected type:', typeof dbConfig?.configuration_data);
        configData = {};
      }

      // Build plannerData object in the format pdfGenerator expects
      const plannerData = {
        personalization: {
          name: configData?.personalization?.name || 'My Planner',
          year: configData?.personalization?.year || new Date()?.getFullYear(),
          quote: configData?.personalization?.quote || '',
          includeCover: configData?.personalization?.includeCover ?? true,
          includeIndex: configData?.personalization?.includeIndex ?? true,
          includeCalendar: configData?.personalization?.includeCalendar ?? true,
          includeGoals: configData?.personalization?.includeGoals ?? true,
          includePageNumbers: configData?.personalization?.includePageNumbers ?? true,
          includeBottomQuotes: configData?.personalization?.includeBottomQuotes ?? true,
          quoteFrequency: configData?.personalization?.quoteFrequency || 'daily',
          selectedQuoteSet: configData?.personalization?.selectedQuoteSet || 'motivational',
          font: configData?.personalization?.font || 'inriaserif',
          titleFont: configData?.personalization?.titleFont || null,
          backgroundText: configData?.personalization?.backgroundText || ''
        },
        layout: layoutConfig,
        size: dbConfig?.paper_size || 'A4',
        theme: dbConfig?.color_scheme || 'classic',
        timeFormat: configData?.timeFormat || '24h',
        timeRange: configData?.timeRange || { start: '06:00', end: '22:00' }
      };

      console.log('PDF_PROGRESS: Data transformation complete');
      return plannerData;
    } catch (error) {
      console.error('PDF_PROGRESS: ERROR - Data transformation failed:', error?.message);
      throw new Error(`Configuration transformation failed: ${error?.message}`);
    }
  },

  /**
   * Regenerates a planner from database configuration
   * @param {string} plannerId - Planner configuration ID
   * @param {Function} onProgress - Callback for progress updates (log, progress, totalPages)
   * @returns {Promise<Object>} - { success, storageUrl, blobUrl }
   */
  async regeneratePlanner(plannerId, onProgress) {
    try {
      console.log('PDF_PROGRESS: Starting planner regeneration...');
      onProgress?.({ message: 'Starting planner regeneration...', progress: 0 });

      // Step 1: Fetch configuration from database
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('PDF_PROGRESS: Fetching planner configuration...');
      onProgress?.({ message: 'Fetching planner configuration...', progress: 5 });

      const { data: plannerConfig, error: fetchError } = await supabase?.from('planner_configurations')?.select('*')?.eq('id', plannerId)?.eq('user_id', user?.id)?.single();

      if (fetchError) throw fetchError;
      if (!plannerConfig) throw new Error('Planner configuration not found');

      console.log('PDF_PROGRESS: Configuration loaded successfully');
      onProgress?.({ message: 'Configuration loaded successfully', progress: 10 });

      // Step 2: Transform database config to pdfGenerator format
      console.log('PDF_PROGRESS: Transforming configuration data...');
      onProgress?.({ message: 'Transforming configuration data...', progress: 15 });

      const plannerData = this.transformDatabaseConfig(plannerConfig);
      onProgress?.({ message: 'Configuration transformed successfully', progress: 20 });

      // Step 3: Capture console.log and generate PDF
      console.log('PDF_PROGRESS: Initializing PDF generator...');
      onProgress?.({ message: 'Initializing PDF generator...', progress: 25 });

      const capturedLogs = [];
      const originalConsoleLog = console.log;
      let currentProgress = 25;
      let estimatedTotalPages = 0;

      // Intercept console.log to capture PDF_PROGRESS messages
      console.log = function(...args) {
        originalConsoleLog?.apply(console, args);
        
        const message = args?.join(' ');
        if (message?.includes('PDF_PROGRESS:')) {
          // Remove prefix for display
          const cleanMessage = message?.replace('PDF_PROGRESS:', '')?.trim();
          capturedLogs?.push(cleanMessage);
          
          // Extract page information for progress calculation
          if (message?.includes('total pages')) {
            const match = message?.match(/(\d+) total pages/);
            if (match) {
              estimatedTotalPages = parseInt(match?.[1]);
            }
          }
          
          // Calculate progress based on page generation
          if (message?.includes('Generated') && message?.includes('of') && estimatedTotalPages > 0) {
            const match = message?.match(/Generated (\d+) of (\d+)/);
            if (match) {
              const currentPage = parseInt(match?.[1]);
              const total = parseInt(match?.[2]);
              // Progress from 25% to 80% during PDF generation
              currentProgress = 25 + Math.floor((currentPage / total) * 55);
            }
          }
          
          onProgress?.({ 
            message: cleanMessage, 
            progress: currentProgress,
            totalPages: estimatedTotalPages 
          });
        }
      };

      try {
        // Generate PDF
        const { blob } = await generatePlannerPDF({
          plannerData,
          specifications: {},
          includeYearView: plannerData?.personalization?.includeCalendar !== false,
          mode: 'full'
        });

        // Restore original console.log
        console.log = originalConsoleLog;

        console.log('PDF_PROGRESS: PDF generation complete');
        onProgress?.({ message: 'PDF generation complete', progress: 85 });

        // Step 4: Upload to Supabase storage
        console.log('PDF_PROGRESS: Uploading to cloud storage...');
        onProgress?.({ message: 'Uploading to cloud storage...', progress: 90 });

        const uploadResult = await plannerStorageService?.uploadFullPlanner(
          blob,
          plannerData,
          plannerId
        );

        if (!uploadResult?.success) {
          throw new Error(uploadResult?.uploadError || 'Upload failed');
        }

        console.log('PDF_PROGRESS: Upload complete');
        onProgress?.({ message: 'Upload complete', progress: 95 });

        // Step 5: Update database with new URL
        console.log('PDF_PROGRESS: Updating database...');
        onProgress?.({ message: 'Updating database...', progress: 98 });

        const { error: updateError } = await supabase?.from('planner_configurations')?.update({ 
            generated_pdf_path: uploadResult?.storagePath,
            updated_at: new Date()?.toISOString()
          })?.eq('id', plannerId)?.eq('user_id', user?.id);

        if (updateError) throw updateError;

        console.log('PDF_PROGRESS: Regeneration complete!');
        onProgress?.({ message: 'Regeneration complete!', progress: 100 });

        return {
          success: true,
          storageUrl: uploadResult?.storageUrl,
          blobUrl: uploadResult?.blobUrl,
          plannerData: plannerData
        };

      } catch (pdfError) {
        // Restore original console.log on error
        console.log = originalConsoleLog;
        throw pdfError;
      }

    } catch (error) {
      console.error('PDF_PROGRESS: ERROR -', error?.message);
      onProgress?.({ message: `ERROR: ${error?.message}`, progress: 0 });
      throw error;
    }
  }
};